import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { intellisense } from '~/api';
import featureFlagConstants from '~/common/helpers/featureFlagConstants';
import { getFeatureFlag } from '~/common/helpers/globalFeatureFlags';
import { usePipelineStore } from '~/scenes/Build/scenes/FlowBuilder/stores/pipelineStore';

export type GlobalsState = {
  globals: string[];
  isLoading: boolean;
  error: Error | null;
};

export type GlobalsActions = {
  fetchGlobals: () => Promise<void>;
  setGlobals: (globals: string[]) => void;
  reset: () => void;
};

export type GlobalsStore = GlobalsState & GlobalsActions;

const initialState: GlobalsState = {
  globals: [],
  isLoading: false,
  error: null
};

export const useGlobalsStore = create<GlobalsStore>()(
  devtools(
    immer(set => ({
      ...initialState,

      fetchGlobals: async () => {
        set(state => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error - window.store typing from legacy Redux store
          const { realm } = window.store.getState().application.realm;
          const { projectId } = usePipelineStore.getState().pipeline || {};

          const isGovernanceProjectAssociationEnabled = getFeatureFlag(
            featureFlagConstants.GOVERNANCE_PROJECT_2_0_ASSOCIATION
          );

          const completions = await intellisense.fetch(
            realm,
            isGovernanceProjectAssociationEnabled ? (projectId ?? null) : null
          );

          const globals = completions.map(
            (global: { label: string }) => global.label
          );

          set(state => {
            state.globals = globals;
            state.isLoading = false;
          });
        } catch (error) {
          set(state => {
            state.error = error as Error;
            state.isLoading = false;
          });
        }
      },

      setGlobals: globals => {
        set(state => {
          state.globals = globals;
        });
      },

      reset: () => {
        set(initialState);
      }
    })),
    { name: 'GlobalsStore' }
  )
);

export const fetchGlobals = () => useGlobalsStore.getState().fetchGlobals();
export const getGlobals = () => useGlobalsStore.getState().globals;
export const setGlobals = (globals: string[]) =>
  useGlobalsStore.getState().setGlobals(globals);
export const resetGlobalsStore = () => useGlobalsStore.getState().reset();

export const useGlobals = () => useGlobalsStore(state => state.globals);
export const useGlobalsLoading = () =>
  useGlobalsStore(state => state.isLoading);
export const useGlobalsError = () => useGlobalsStore(state => state.error);
